import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { getQaSettings, updateQaSettings, revokeQaDevice, grantQaDevice, updateQaDevice } from '@/api/qa';
import { COUNTRIES } from '@/lib/countries';
import { BuildVerification } from './qa/BuildVerification';
import { AppBans } from './qa/AppBans';

type QaFlags = { banners: boolean; webview: boolean; antifraud: boolean; excludeStats: boolean };
const DEFAULT_FLAGS: QaFlags = { banners: true, webview: true, antifraud: true, excludeStats: true };

function parseFlags(raw: string | undefined): QaFlags {
  if (!raw) return { ...DEFAULT_FLAGS };
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    const f = (v: unknown) => (typeof v === 'boolean' ? v : true);
    return { banners: f(o.banners), webview: f(o.webview), antifraud: f(o.antifraud), excludeStats: f(o.excludeStats) };
  } catch {
    return { ...DEFAULT_FLAGS };
  }
}

const TOGGLES: { key: keyof QaFlags; label: string; help: string }[] = [
  { key: 'banners', label: 'Показывать наши рекламные баннеры', help: 'Обходит гейты показа рекламы (review-ban, unknown-model, version-review) в ads.ts.' },
  { key: 'webview', label: 'Показывать форс-вебвью (force offer)', help: 'Форс-офер всегда срабатывает в referral.ts, минуя antifraud.' },
  { key: 'antifraud', label: 'Обходить antifraud на устройстве', help: 'Отчёт устройства не блокируется (no_sim / geo_mismatch и т.п.) в device.ts.' },
  { key: 'excludeStats', label: 'Не писать события в статистику', help: 'Импрессии/клики с этих IP не попадают в events_raw.' },
];

export function Qa() {
  const queryClient = useQueryClient();
  const [ips, setIps] = useState('');
  const [flags, setFlags] = useState<QaFlags>({ ...DEFAULT_FLAGS });
  const [forcedGeo, setForcedGeo] = useState('');
  const [ttlHours, setTtlHours] = useState('24');
  const [saved, setSaved] = useState(false);
  const [deviceId, setDeviceId] = useState('');
  const [deviceTtlHours, setDeviceTtlHours] = useState('24');
  const [deviceForceOfferDisabled, setDeviceForceOfferDisabled] = useState(false);

  const { data: qa, isLoading } = useQuery({ queryKey: ['qa-settings'], queryFn: getQaSettings });
  const myIp = qa?.ip;

  useEffect(() => {
    if (qa) {
      setIps(qa.settings.test_whitelist_ips || '');
      setFlags(parseFlags(qa.settings.qa_whitelist_flags));
      setForcedGeo(qa.settings.qa_forced_geo || '');
      setTtlHours(qa.settings.qa_device_ttl_hours || '24');
      setDeviceTtlHours((current) => current === '24' ? (qa.settings.qa_device_ttl_hours || '24') : current);
    }
  }, [qa]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, string>) => updateQaSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (deviceId: string) => revokeQaDevice(deviceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qa-settings'] }),
  });

  const grantMutation = useMutation({
    mutationFn: () => grantQaDevice({
      device_id: deviceId.trim(),
      ttl_hours: Number(deviceTtlHours),
      force_offer_disabled: deviceForceOfferDisabled,
    }),
    onSuccess: () => {
      setDeviceId('');
      queryClient.invalidateQueries({ queryKey: ['qa-settings'] });
    },
  });

  const deviceFlagMutation = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) => updateQaDevice(id, disabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qa-settings'] }),
  });

  const handleSave = () => {
    saveMutation.mutate({
      test_whitelist_ips: ips, // always write (even empty) so the list can be cleared
      qa_whitelist_flags: JSON.stringify(flags),
      qa_forced_geo: forcedGeo, // '' clears the override
      qa_device_ttl_hours: ttlHours,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <h1 className="text-2xl font-semibold text-content mb-6">QA</h1>

      <div className="bg-surface border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">Тестовый вайтлист</h2>
          <p className="text-sm text-content-subtle mt-1">
            IP/CIDR наших устройств и что именно им разрешено. Изменения применяются в течение ~5 минут.
          </p>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label htmlFor="qa-whitelist-ips" className="block text-sm font-medium text-content-muted mb-1">
              Тестовые IP (вайтлист)
            </label>
            <p className="text-xs text-content-subtle mb-2">
              По одному IP или CIDR в строке.{myIp ? ` Ваш текущий IP: ${myIp}` : ''}
            </p>
            <textarea
              id="qa-whitelist-ips"
              rows={5}
              value={ips}
              onChange={(e) => setIps(e.target.value)}
              placeholder={'203.0.113.7\n198.51.100.0/24'}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
            />
          </div>

          <div>
            <p className="block text-sm font-medium text-content-muted mb-2">Что разрешено вайтлист-IP</p>
            <div className="space-y-3">
              {TOGGLES.map((t) => (
                <label key={t.key} htmlFor={`qa-flag-${t.key}`} className="flex items-start gap-3 cursor-pointer">
                  <input
                    id={`qa-flag-${t.key}`}
                    type="checkbox"
                    checked={flags[t.key]}
                    onChange={(e) => setFlags((prev) => ({ ...prev, [t.key]: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                  />
                  <span>
                    <span className="block text-sm text-content">{t.label}</span>
                    <span className="block text-xs text-content-subtle">{t.help}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="qa-forced-geo" className="block text-sm font-medium text-content-muted mb-1">
              Форс-гео для тестовых устройств
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Любое whitelisted-устройство попадёт на флоу этой страны независимо от реального
              гео — включая форс-вебвью. Пусто = не форсить.
            </p>
            <select
              id="qa-forced-geo"
              value={forcedGeo}
              onChange={(e) => setForcedGeo(e.target.value)}
              className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            >
              <option value="">— не форсить —</option>
              {COUNTRIES.map((ctry) => (
                <option key={ctry.code} value={ctry.code}>
                  {ctry.flag} {ctry.name} ({ctry.code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="qa-device-ttl" className="block text-sm font-medium text-content-muted mb-1">
              Срок QA-сессии устройства, часов
            </label>
            <p className="text-xs text-content-subtle mb-2">
              Устройство, зашедшее с вайтлист-IP, остаётся тестовым столько часов даже после
              смены IP — можно выключить VPN и продолжать тест. Каждый заход с вайтлист-IP
              продлевает срок заново. 1–720, по умолчанию 24.
            </p>
            <input
              id="qa-device-ttl"
              type="number"
              min={1}
              max={720}
              value={ttlHours}
              onChange={(e) => setTtlHours(e.target.value)}
              className="block w-32 px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-between">
          <div>
            {saved && <span className="text-sm text-success">Saved</span>}
            {saveMutation.isError && (
              <span className="text-sm text-danger">
                Failed to save: {saveMutation.error instanceof Error ? saveMutation.error.message : 'Unknown error'}
              </span>
            )}
          </div>
          <Button onClick={handleSave} loading={saveMutation.isPending}>Save</Button>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg mt-6">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium text-content">Активные QA-устройства</h2>
          <p className="text-sm text-content-subtle mt-1">
            Устройства, которые заходили с вайтлист-IP и пока остаются тестовыми с любого IP.
            Выдача и отзыв применяются в течение минуты.
          </p>
        </div>

        <div className="px-6 py-5 border-b border-border">
          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_120px_auto] md:items-end">
            <div>
              <label htmlFor="qa-device-id" className="block text-sm font-medium text-content-muted mb-1">
                Device ID
              </label>
              <input
                id="qa-device-id"
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                placeholder="d7b85f7e-02d3-4fef-ab59-7009669f45c5"
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm font-mono"
              />
            </div>
            <div>
              <label htmlFor="qa-device-manual-ttl" className="block text-sm font-medium text-content-muted mb-1">
                Срок, часов
              </label>
              <input
                id="qa-device-manual-ttl"
                type="number"
                min={1}
                max={720}
                value={deviceTtlHours}
                onChange={(e) => setDeviceTtlHours(e.target.value)}
                className="block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>
            <Button
              onClick={() => grantMutation.mutate()}
              loading={grantMutation.isPending}
              disabled={!deviceId.trim() || !deviceTtlHours}
            >
              Добавить / продлить
            </Button>
          </div>
          <label htmlFor="qa-device-disable-force" className="mt-4 flex items-start gap-3 cursor-pointer">
            <input
              id="qa-device-disable-force"
              type="checkbox"
              checked={deviceForceOfferDisabled}
              onChange={(e) => setDeviceForceOfferDisabled(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span>
              <span className="block text-sm text-content">Отключить force offer только для этого устройства</span>
              <span className="block text-xs text-content-subtle">Обычные рекламные баннеры продолжат показываться по QA-правилам.</span>
            </span>
          </label>
          {grantMutation.isError && (
            <p className="mt-3 text-sm text-danger">
              Не удалось добавить устройство: {grantMutation.error instanceof Error ? grantMutation.error.message : 'Unknown error'}
            </p>
          )}
        </div>

        {(qa?.devices?.length ?? 0) === 0 ? (
          <p className="px-6 py-5 text-sm text-content-subtle">
            Пока пусто. Добавьте Device ID выше или зайдите в приложение с вайтлист-IP.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-content-subtle border-b border-border">
                  <th className="px-6 py-2 font-medium">Device ID</th>
                  <th className="px-6 py-2 font-medium">Последний IP</th>
                  <th className="px-6 py-2 font-medium">Действует до (UTC)</th>
                  <th className="px-6 py-2 font-medium">Force offer</th>
                  <th className="px-6 py-2" />
                </tr>
              </thead>
              <tbody>
                {qa!.devices.map((d) => (
                  <tr key={d.device_id} className="border-b border-border last:border-0">
                    <td className="px-6 py-3 font-mono text-content">{d.device_id}</td>
                    <td className="px-6 py-3 font-mono text-content-muted">{d.last_ip || '—'}</td>
                    <td className="px-6 py-3 text-content-muted">{d.expires_at}</td>
                    <td className="px-6 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={d.force_offer_disabled === 1}
                          onChange={(e) => deviceFlagMutation.mutate({
                            id: d.device_id,
                            disabled: e.target.checked,
                          })}
                          disabled={deviceFlagMutation.isPending && deviceFlagMutation.variables?.id === d.device_id}
                          className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                        />
                        <span className={d.force_offer_disabled === 1 ? 'text-danger' : 'text-content-muted'}>
                          {d.force_offer_disabled === 1 ? 'Отключён' : 'Разрешён'}
                        </span>
                      </label>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Button
                        variant="secondary"
                        onClick={() => revokeMutation.mutate(d.device_id)}
                        loading={revokeMutation.isPending && revokeMutation.variables === d.device_id}
                      >
                        Отозвать
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {revokeMutation.isError && (
          <p className="px-6 pb-4 text-sm text-danger">
            Не удалось отозвать: {revokeMutation.error instanceof Error ? revokeMutation.error.message : 'Unknown error'}
          </p>
        )}
        {deviceFlagMutation.isError && (
          <p className="px-6 pb-4 text-sm text-danger">
            Не удалось изменить force offer: {deviceFlagMutation.error instanceof Error ? deviceFlagMutation.error.message : 'Unknown error'}
          </p>
        )}
      </div>

      <BuildVerification />

      <AppBans />
    </div>
  );
}
